/**
 * Count outcomes (spec C11, §5.3; plan R14): leads, bookings and sales are units with no value per unit, so the page
 * prints them as counts and talks in cost per unit, where lower is better. Pure.
 *
 * @module services/budget/outcomeUnits
 */
import type { TFn } from '../channels/channelFormat';
import { DASH, fmtCount, fmtMoney } from '../overview/overviewFormat';
import type { OutcomeMetric } from './budgetContract';
import type { Ranged } from './bundleSchema';

export type CountMetric = Exclude<OutcomeMetric, 'revenue'>;

/** A cost per unit and its 90% range. hi is null, and unbounded true, where the divisor's range reaches 0 or less. */
export interface CostRange {
  mean: number | null;
  lo: number | null;
  hi: number | null;
  unbounded: boolean;
}

export const isCount = (m: OutcomeMetric): m is CountMetric => m !== 'revenue';

/**
 * numerator ÷ divisor as a cost: only over a divisor above 0, and never past a double's range (1e15 ÷ 1e-300 is
 * Infinity). No MIN_MOVE here: a small positive divisor is a real, large cost, not a zero.
 */
export const costOf = (numerator: number, divisor: number): number | null => {
  if (!(divisor > 0)) return null;
  const q = numerator / divisor;
  return Number.isFinite(q) ? q : null;
};

/** R14: spend ÷ contribution, or 1 ÷ the marginal units per baht, so the range inverts to (n ÷ hi, n ÷ lo). */
export const costRange = (numerator: number, divisor: Ranged): CostRange => {
  const hi = costOf(numerator, divisor.lo);
  return { mean: costOf(numerator, divisor.mean), lo: costOf(numerator, divisor.hi), hi, unbounded: hi === null };
};

/** One unit, as spec §5.3 writes it ("ต้นทุนต่อ booking ถัดไป"). Literal keys, so the locale integrity test sees them. */
export const unitName = (m: CountMetric, t: TFn): string => {
  if (m === 'leads') return t('budget.unit.lead');
  if (m === 'bookings') return t('budget.unit.booking');
  return t('budget.unit.sale');
};

/** The units, lowercase, as a sentence, a title or a column carries them ("Share of bookings"). */
export const unitsName = (m: CountMetric, t: TFn): string => {
  if (m === 'leads') return t('budget.unit.leads');
  if (m === 'bookings') return t('budget.unit.bookings');
  return t('budget.unit.sales');
};

/** The download's client-data line, one branch for box 1 and /admin/mmm-models (T18a): spec §6's revenue words, or the units. */
export const clientDataLine = (m: OutcomeMetric, t: TFn): string =>
  (isCount(m) ? t('budgetAdmin.download.clientDataCount', { units: unitsName(m, t) }) : t('budgetAdmin.download.clientData'));

/** How a figure of the outcome prints: a count as a count, revenue as money in the version's currency. */
export const outcomeFigure = (m: OutcomeMetric, currency: string | null) => (n: number | null): string =>
  (isCount(m) ? fmtCount(n) : fmtMoney(n, currency));

/** An unbounded cost reads "มากกว่า ฿X" from its low end (R14); with no low end either, "—". */
const over = (c: CostRange, money: (n: number) => string, t: TFn): string =>
  (c.lo === null ? DASH : t('budget.cost.over', { amount: money(c.lo) }));

const shown = (n: number | null, money: (n: number) => string): string => (n === null ? DASH : money(n));

/** The cost's figure: its mean, or "มากกว่า ฿X" when its range has no top. */
export const costText = (c: CostRange, money: (n: number) => string, t: TFn): string =>
  (c.unbounded ? over(c, money, t) : shown(c.mean, money));

/** Its 90% range: "฿200.00–฿500.00", or "มากกว่า ฿200.00". */
export const costRangeText = (c: CostRange, money: (n: number) => string, t: TFn): string =>
  (c.unbounded ? over(c, money, t) : `${shown(c.lo, money)}–${shown(c.hi, money)}`);
